/** biome-ignore-all lint/suspicious/noDocumentCookie: <Next.js support> */
import { withRetries } from "@core/utils/withRetries";
import type { GroupValue } from "@ext/enterprise/components/admin/settings/components/roles/Access";
import type { EditorsSettings } from "@ext/enterprise/components/admin/settings/editors/types/EditorsComponentTypes";
import type { GuestsSettings } from "@ext/enterprise/components/admin/settings/guests/types/GuestsComponent";
import type { MailSettings } from "@ext/enterprise/components/admin/settings/MailComponent";
import type { AnonymousFilter } from "@ext/enterprise/components/admin/settings/metrics/filters";
import type { ArticleRatingsResponse } from "@ext/enterprise/components/admin/settings/metrics/search/ratings/ArticleRatingsTableConfig";
import type { SearchTableDataResponse } from "@ext/enterprise/components/admin/settings/metrics/search/table/SearchMetricsTableConfig";
import type {
	ChartDataPoint,
	MetricsCatalogsResponse,
	MetricsConfigSettings,
	MetricsUsersResponse,
	SearchChartDataPoint,
	SearchQueryDetailsResponse,
	TableDataResponse,
} from "@ext/enterprise/components/admin/settings/metrics/types";
import type { QuizTableFilters } from "@ext/enterprise/components/admin/settings/quiz/components/QuizTableControls";
import type { QuizSettings } from "@ext/enterprise/components/admin/settings/quiz/QuizComponent";
import type {
	QuizTest,
	QuizTestData,
	SearchedAnsweredUsers,
	SearchedAnsweredUsersResponse,
	SearchedQuizTest,
	SearchedQuizTestResponse,
} from "@ext/enterprise/components/admin/settings/quiz/types/QuizComponentTypes";
import type { ResourcesSettings } from "@ext/enterprise/components/admin/settings/resources/types/ResourcesComponent";
import type { StyleGuideSettings } from "@ext/enterprise/components/admin/settings/styleGuide/StyleGuideComponent";
import type { WorkspaceSettings } from "@ext/enterprise/components/admin/settings/workspace/types/WorkspaceComponent";
import type {
	RequestCursor,
	RequestData,
} from "@ext/enterprise/components/admin/ui-kit/table/LazyInfinityTable/LazyInfinityTable";
import { GesError, gesErrorCodeByStatus } from "@ext/enterprise/errors/GesError";
import type { Settings } from "@ext/enterprise/types/EnterpriseAdmin";
import t from "@ext/localization/locale/translate";
import { addEvent, Level, trace } from "@ext/loggers/opentelemetry";
import type { CheckChunk, CheckOverrideSettings, CheckSuggestion } from "@ics/gx-vector-search";
import type { PluginsSettings } from "./types/PluginsSettings";

export interface searchUserInfo {
	email: string;
	name: string;
}

export interface searchGroupInfo {
	id: string;
	name: string;
}

export interface NotificationItemFromAPI {
	id: number;
	article_title: string;
	article_url: string;
	catalog_name: string;
	preview_text?: string;
	created_at: string;
	is_read: boolean;
	user_email: string;
	read_at?: string;
	delivered_at?: string;
}

export interface NotificationHistoryResponse {
	items: NotificationItemFromAPI[];
	hasMore: boolean;
	cursor: { created_at: string; id: number } | null;
}

export interface EtagResult<T> {
	data: T | null;
	etag: string | null;
	notModified: boolean;
}

export interface LicenseInfo {
	isValid: boolean;
	editorCount?: number;
	expirationDate?: string;
	unlimitedEditors?: boolean;
	occupiedEditors: number;
}

type ConfigTabMap = Omit<Settings, "searchMetrics">;

const specialConfigNameMap: Partial<Record<keyof ConfigTabMap, string>> = {
	styleGuide: "style-guide",
	metrics: "metrics-config",
};

const REQUEST_RETRY_COUNT = 3;
const REQUEST_RETRY_DELAY_MS = 1000;

const isRetryableStatus = (status: number): boolean => status === 408 || status === 429 || status >= 500;

class EnterpriseService {
	constructor(private _url: string) {}

	getGesUrl(): string {
		return this._url;
	}

	private _clearUserCookie(): void {
		document.cookie = "userInfo=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
		document.cookie = "user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
	}

	private _checkUnauthorized(response: Response): void {
		if (response.status === 401 || response.status === 403) {
			this._clearUserCookie();
		}
	}

	async isAdmin(token: string): Promise<boolean> {
		const res = await fetch(`${this._url}/enterprise/config/check`, {
			headers: { Authorization: `Bearer ${token}` },
			credentials: "include",
		});
		this._checkUnauthorized(res);
		return res.status !== 401 && res.status !== 403;
	}

	async getLicenseInfo(token: string): Promise<LicenseInfo> {
		return this._fetchGet<LicenseInfo>("/enterprise/config/license/get", token);
	}

	async checkDataProviderHealth(): Promise<boolean> {
		try {
			const res = await fetch(`${this._url}/enterprise/data-provider-health`);
			if (!res.ok) return false;
			return res.status === 200;
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	async checkStyleGuideHealth(): Promise<boolean> {
		try {
			const res = await fetch(`${this._url}/enterprise/style-guide/health`);
			if (!res.ok) return false;
			return res.status === 200;
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	// `token` is a bare parameter — the encoder sees a positional array and has no key name to mask by
	@trace({ level: Level.Commands, omitArgs: true })
	async markNotificationsAsRead(userEmail: string, notificationIds: number[], token: string): Promise<boolean> {
		try {
			const res = await fetch(`${this._url}/enterprise/notifications/mark-read`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
				credentials: "include",
				body: JSON.stringify({
					email: userEmail,
					notificationIds,
				}),
			});

			this._checkUnauthorized(res);
			return res.ok;
		} catch (error) {
			addEvent("error", Level.Commands, { error: String(error) });
			return false;
		}
	}

	// `token` is a bare parameter — the encoder sees a positional array and has no key name to mask by
	@trace({ level: Level.Internal, omitArgs: true })
	async fetchNotificationHistory(
		userEmail: string,
		token: string,
		opts?: { cursor?: { created_at: string; id: number }; limit?: number },
	): Promise<NotificationHistoryResponse | null> {
		try {
			let url = `${this._url}/enterprise/notifications/history?email=${encodeURIComponent(userEmail)}&unreadOnly=false&limit=${opts?.limit ?? 10}`;
			if (opts?.cursor) {
				url += `&cursor=${encodeURIComponent(JSON.stringify(opts.cursor))}`;
			}

			const res = await fetch(url, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
				credentials: "include",
			});

			this._checkUnauthorized(res);
			if (!res.ok) return null;

			return (await res.json()) as NotificationHistoryResponse;
		} catch (error) {
			addEvent("error", Level.Commands, { error: String(error) });
			return null;
		}
	}

	// `token` is a bare parameter — the encoder sees a positional array and has no key name to mask by
	@trace({ level: Level.Internal, omitArgs: true })
	async getResourceConfig(token: string, resourceId: string): Promise<ResourcesSettings | null> {
		if (!this._url || !token) return null;
		try {
			const url = `${this._url}/enterprise/config/resources/getOne?resourceId=${encodeURIComponent(resourceId)}`;
			const res = await fetch(url, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
				credentials: "include",
			});

			this._checkUnauthorized(res);

			if (!res.ok) {
				addEvent("error", Level.Commands, { error: `Failed to get resource config: ${res.status}` });
				return null;
			}

			return await res.json();
		} catch (error) {
			addEvent("error", Level.Commands, { error: String(error) });
			return null;
		}
	}

	async getMetricsChartData(
		token: string,
		startDate: string,
		endDate: string,
		articleIds?: number[],
		userEmails?: string[],
		anonymousFilter?: AnonymousFilter,
		catalogFilter?: string[],
	): Promise<ChartDataPoint[]> {
		const params = new URLSearchParams({ startDate, endDate });
		if (articleIds?.length) params.append("articleIds", articleIds.join(","));
		if (userEmails?.length) params.append("userEmails", userEmails.join(","));
		if (anonymousFilter) params.append("anonymousFilter", anonymousFilter);
		if (catalogFilter?.length) params.append("catalogFilter", catalogFilter.join(","));
		return this._fetchGet<ChartDataPoint[]>(`/enterprise/modules/metrics/getChartData?${params.toString()}`, token);
	}

	async getSearchMetricsTableData(
		token: string,
		startDate: string,
		endDate: string,
		cursor?: string,
		sortBy?: string,
		sortOrder?: string,
		limit?: number,
		catalogFilter?: string[],
	): Promise<SearchTableDataResponse> {
		const params = new URLSearchParams({ startDate, endDate });
		if (cursor) params.append("cursor", cursor);
		if (sortBy) params.append("sortBy", sortBy);
		if (sortOrder) params.append("sortOrder", sortOrder);
		if (limit) params.append("limit", limit.toString());
		if (catalogFilter?.length) params.append("catalogFilter", catalogFilter.join(","));
		return this._fetchGet<SearchTableDataResponse>(
			`/enterprise/modules/metrics/getSearchTableData?${params.toString()}`,
			token,
		);
	}

	async getSearchQueryDetails(
		token: string,
		query: string,
		startDate: string,
		endDate: string,
		cursor?: number,
		sortBy?: string,
		sortOrder?: string,
		limit?: number,
	): Promise<SearchQueryDetailsResponse> {
		const params = new URLSearchParams({ query, startDate, endDate });
		if (cursor) params.append("cursor", String(cursor));
		if (sortBy) params.append("sortBy", sortBy);
		if (sortOrder) params.append("sortOrder", sortOrder);
		if (limit) params.append("limit", limit.toString());
		return this._fetchGet<SearchQueryDetailsResponse>(
			`/enterprise/modules/metrics/getSearchQueryDetails?${params.toString()}`,
			token,
		);
	}

	async getArticleRatings(
		token: string,
		startDate: string,
		endDate: string,
		cursor?: string,
		sortBy?: string,
		sortOrder?: string,
		limit?: number,
		catalogFilter?: string[],
	): Promise<ArticleRatingsResponse> {
		const params = new URLSearchParams({ startDate, endDate });
		if (cursor) params.append("cursor", cursor);
		if (sortBy) params.append("sortBy", sortBy);
		if (sortOrder) params.append("sortOrder", sortOrder);
		if (limit) params.append("limit", limit.toString());
		if (catalogFilter?.length) params.append("catalogFilter", catalogFilter.join(","));
		return this._fetchGet<ArticleRatingsResponse>(
			`/enterprise/modules/metrics/getArticleRatings?${params.toString()}`,
			token,
		);
	}

	async getSearchMetricsChartData(
		token: string,
		startDate: string,
		endDate: string,
		catalogFilter?: string[],
	): Promise<SearchChartDataPoint[]> {
		const params = new URLSearchParams({ startDate, endDate });
		if (catalogFilter?.length) params.append("catalogFilter", catalogFilter.join(","));
		return this._fetchGet<SearchChartDataPoint[]>(
			`/enterprise/modules/metrics/getSearchChartData?${params.toString()}`,
			token,
		);
	}

	async getMetricsTableData(
		token: string,
		startDate: string,
		endDate: string,
		limit: number,
		cursor?: number,
		sortBy?: string,
		sortOrder?: string,
		userEmails?: string[],
		anonymousFilter?: AnonymousFilter,
		catalogFilter?: string[],
	): Promise<TableDataResponse> {
		const params = new URLSearchParams({ startDate, endDate, limit: String(limit) });
		if (cursor !== undefined) params.set("cursor", String(cursor));
		if (sortBy) params.set("sortBy", sortBy);
		if (sortOrder) params.set("sortOrder", sortOrder);
		if (userEmails?.length) params.set("userEmails", userEmails.join(","));
		if (anonymousFilter) params.set("anonymousFilter", anonymousFilter);
		if (catalogFilter?.length) params.set("catalogFilter", catalogFilter.join(","));
		return this._fetchGet<TableDataResponse>(
			`/enterprise/modules/metrics/getTableData?${params.toString()}`,
			token,
		);
	}

	async getMetricsUsers(
		token: string,
		search?: string,
		limit?: number,
		cursor?: number,
	): Promise<MetricsUsersResponse> {
		const params = new URLSearchParams();
		if (search) params.append("search", search);
		if (limit !== undefined) params.append("limit", limit.toString());
		if (cursor !== undefined) params.append("cursor", cursor.toString());
		return this._fetchGet<MetricsUsersResponse>(
			`/enterprise/modules/metrics/getMetricsUsers?${params.toString()}`,
			token,
		);
	}

	async getMetricsCatalogs(
		token: string,
		search?: string,
		limit?: number,
		cursor?: number,
	): Promise<MetricsCatalogsResponse> {
		const params = new URLSearchParams();
		if (search) params.append("search", search);
		if (limit !== undefined) params.append("limit", limit.toString());
		if (cursor !== undefined) params.append("cursor", cursor.toString());
		return this._fetchGet<MetricsCatalogsResponse>(
			`/enterprise/modules/metrics/getMetricsCatalogs?${params.toString()}`,
			token,
		);
	}

	async getSearchMetricsCatalogs(
		token: string,
		search?: string,
		limit?: number,
		cursor?: number,
	): Promise<MetricsCatalogsResponse> {
		const params = new URLSearchParams();
		if (search) params.append("search", search);
		if (limit !== undefined) params.append("limit", limit.toString());
		if (cursor !== undefined) params.append("cursor", cursor.toString());
		return this._fetchGet<MetricsCatalogsResponse>(
			`/enterprise/modules/metrics/getSearchMetricsCatalogs?${params.toString()}`,
			token,
		);
	}

	getResources(token: string, page: number): Promise<{ repos: string[]; total: number }> {
		return this._fetchGet<{ repos: string[]; total: number }>(`/enterprise/git/get-repos?page=${page}`, token);
	}

	getBranches(token: string, repoName: string): Promise<string[]> {
		return this._fetchGet<string[]>(`/enterprise/git/get-branches?repoName=${encodeURIComponent(repoName)}`, token);
	}

	setWorkspace(token: string, workspace: WorkspaceSettings) {
		return this._postConfig("workspace/set", token, workspace);
	}

	addGroup(token: string, group: { groupId: string; groupValue: GroupValue[]; groupName?: string }) {
		return this._postConfig("groups/add", token, group);
	}

	deleteGroups(token: string, groupIds: string[]) {
		return this._postConfig("groups/delete", token, { groupIds });
	}

	renameGroup(token: string, groupId: string, newName: string) {
		return this._postConfig("groups/rename", token, { groupId, newName });
	}

	setEditors(token: string, editors: EditorsSettings) {
		return this._postConfig("editors/set", token, editors);
	}

	addResource(token: string, resource: ResourcesSettings) {
		return this._postConfig("resources/add", token, resource);
	}

	deleteResources(token: string, resourceIds: string[]) {
		return this._postConfig("resources/delete", token, { resourceIds });
	}

	setMail(token: string, mail: MailSettings) {
		return this._postConfig("mail/set", token, mail);
	}

	setGuests(token: string, guests: GuestsSettings) {
		return this._postConfig("guests/set", token, guests);
	}

	setStyleGuide(token: string, styleGuide: StyleGuideSettings) {
		return this._postConfig("style-guide/set", token, styleGuide);
	}

	async checkStyleGuide(
		chunks: CheckChunk[],
		providers: ["languageTool" | "llm"],
		overrideSettings?: CheckOverrideSettings,
		checkSpelling?: boolean,
		signal?: AbortSignal,
	): Promise<CheckSuggestion[]> {
		try {
			const res = await fetch(`${this._url}/enterprise/style-guide/check`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					chunks,
					providers: providers,
					overrideSettings,
					checkSpelling,
				}),
				credentials: "include",
				signal,
			});

			if (res.status === 503) throw new Error(t("enterprise.admin.check.service-unavailable"));
			if (!res.ok) throw new Error(t("enterprise.admin.check.check-failed"));

			const response = await res.json();
			return response;
		} catch (e) {
			console.error(e);
			throw e;
		}
	}

	setQuizConfig(token: string, quiz: QuizSettings) {
		return this._postConfig("quiz/set", token, quiz);
	}

	setMetricsConfig(token: string, metrics: MetricsConfigSettings) {
		return this._postConfig("metrics-config/set", token, metrics);
	}

	setPlugins(token: string, plugins: PluginsSettings) {
		return this._postConfig("plugins/set", token, plugins);
	}

	async checkConnector(): Promise<boolean> {
		if (!this._url) return false;
		try {
			const res = await fetch(`${this._url}/sso/connectors/enabled`, { credentials: "include" });
			return res.ok;
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	async checkGroupsConnector(): Promise<boolean> {
		if (!this._url) return false;
		try {
			const res = await fetch(`${this._url}/sso/connectors/groups/enabled`, { credentials: "include" });
			return res.ok;
		} catch (error) {
			console.error(error);
			return false;
		}
	}

	async getQuizUsersAnswers(
		token: string,
		limit: number,
		cursor: RequestCursor,
		filters?: QuizTableFilters,
	): Promise<RequestData<QuizTest>> {
		try {
			const baseUrl = `${this._url}/enterprise/modules/quiz/answer/get-with-user`;
			const params = new URLSearchParams({
				limit: limit.toString(),
			});

			if (filters?.users.length) params.set("users", encodeURIComponent(JSON.stringify(filters.users)));
			if (filters?.tests?.length) params.set("tests", encodeURIComponent(JSON.stringify(filters.tests)));
			if (filters?.result?.length) params.set("result", encodeURIComponent(JSON.stringify(filters.result)));
			if (cursor) params.set("cursor", encodeURIComponent(JSON.stringify(cursor)));

			const res = await fetch(`${baseUrl}?${params.toString()}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});
			return res.ok ? await res.json() : { data: [], has_more: false, next_cursor: null };
		} catch (error) {
			console.error(error);
			return { data: [], has_more: false, next_cursor: null };
		}
	}

	async searchQuizTest(token: string, query: string): Promise<SearchedQuizTest[]> {
		try {
			const params = new URLSearchParams({
				select: '["title"]',
				distinct: "true",
			});
			if (query) params.set("query", query);

			const url = `${this._url}/enterprise/modules/quiz/test/get?${params.toString()}`;

			const res = await fetch(url, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!res.ok) return [];

			const data: SearchedQuizTestResponse = await res.json();
			return data?.data || [];
		} catch (error) {
			console.error(error);
			return [];
		}
	}

	async searchQuizAnsweredUsers(token: string, query: string): Promise<SearchedAnsweredUsers[]> {
		try {
			const params = new URLSearchParams({
				select: '["user_mail"]',
				distinct: "true",
			});
			if (query) params.set("query", query);

			const url = `${this._url}/enterprise/modules/quiz/answer/user/get?${params.toString()}`;

			const res = await fetch(url, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!res.ok) return [];

			const data: SearchedAnsweredUsersResponse = await res.json();
			return data?.data || [];
		} catch (error) {
			console.error(error);
			return [];
		}
	}

	async getQuizDetailedUserAnswers(token: string, id: number): Promise<QuizTestData> {
		try {
			const params = new URLSearchParams({
				id: String(id),
				select: '["answers", "qt.questions as questions"]',
			});

			const url = `${this._url}/enterprise/modules/quiz/answer/get?${params.toString()}`;

			const res = await fetch(url, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!res.ok) return { answers: null, questions: null };

			const json = await res.json();

			return {
				answers: json.data?.[0].answers,
				questions: json.data?.[0].questions,
			};
		} catch (error) {
			console.error(error);
			return { answers: null, questions: null };
		}
	}

	async getUsers(query: string, token: string): Promise<searchUserInfo[]> {
		try {
			const res = await fetch(`${this._url}/sso/connectors/getUsers`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ emailOrCn: query }),
				credentials: "include",
			});
			if (!res.ok) return [];
			const json = await res.json();
			return Array.isArray(json) ? json : [];
		} catch {
			return [];
		}
	}

	async getGroups(query: string, token: string): Promise<searchGroupInfo[]> {
		try {
			const res = await fetch(`${this._url}/sso/connectors/getGroups`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ groupName: query }),
				credentials: "include",
			});
			if (!res.ok) return [];
			const json = await res.json();
			return Array.isArray(json) ? json : [];
		} catch {
			return [];
		}
	}

	async getGroupsByIds(ids: string[], token: string): Promise<searchGroupInfo[]> {
		if (!ids.length) return [];
		try {
			const res = await fetch(`${this._url}/sso/connectors/getGroupsByIds`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ ids }),
				credentials: "include",
			});
			if (!res.ok) return [];
			const json = await res.json();
			return Array.isArray(json) ? json : [];
		} catch {
			return [];
		}
	}

	async getUsersByEmails(emails: string[], token: string): Promise<searchUserInfo[]> {
		if (!emails.length) return [];
		try {
			const res = await fetch(`${this._url}/sso/connectors/getUsersByEmails`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ emails }),
				credentials: "include",
			});
			if (!res.ok) return [];
			const json = await res.json();
			return Array.isArray(json) ? json : [];
		} catch {
			return [];
		}
	}

	async getConfig<T extends keyof ConfigTabMap>(
		tab: T,
		token: string,
		etag?: string,
	): Promise<EtagResult<ConfigTabMap[T]>> {
		return await this._getConfig<ConfigTabMap[T]>(specialConfigNameMap[tab] ?? tab, token, etag);
	}

	private async _fetchGet<T>(path: string, token: string): Promise<T> {
		if (!this._url) throw new GesError("not-configured");
		const response = await this._request(`${this._url}${path}`, {
			headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
			credentials: "include",
		});
		this._checkUnauthorized(response);
		if (!response.ok) throw new GesError(gesErrorCodeByStatus(response.status));
		return this._parseJson<T>(response);
	}

	private _getConfig<T>(name: string, token: string, etag?: string): Promise<EtagResult<T>> {
		return this._getWithEtag<T>(`${this._url}/enterprise/config/${name}/get`, token, etag);
	}

	private async _getWithEtag<T>(url: string, token: string, etag?: string): Promise<EtagResult<T | null>> {
		if (!this._url || !token) throw new GesError("not-configured");

		const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
		if (etag) headers["If-None-Match"] = etag;

		const res = await this._request(url, { headers, credentials: "include" });
		this._checkUnauthorized(res);

		if (res.status === 304) return { data: null, etag: etag ?? null, notModified: true };
		if (!res.ok) throw new GesError(gesErrorCodeByStatus(res.status));

		return { data: await this._parseJson<T>(res), etag: res.headers.get("ETag"), notModified: false };
	}

	private async _postConfig(endpoint: string, token: string, body: unknown): Promise<void> {
		if (!this._url) throw new GesError("not-configured");

		const res = await this._request(`${this._url}/enterprise/config/${endpoint}`, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(body),
			credentials: "include",
		});
		this._checkUnauthorized(res);
		if (!res.ok) throw new GesError(gesErrorCodeByStatus(res.status));
	}

	private async _request(url: string, init: RequestInit): Promise<Response> {
		let lastResponse: Response;
		let lastError: unknown;

		const response = await withRetries<Response>(
			async () => {
				try {
					const res = await fetch(url, init);
					if (!isRetryableStatus(res.status)) return { type: "success", value: res };
					lastResponse = res;
				} catch (error) {
					lastError = error;
				}
				return { type: "continue" };
			},
			REQUEST_RETRY_COUNT,
			REQUEST_RETRY_DELAY_MS,
		);

		if (response) return response;
		if (lastResponse) return lastResponse;
		throw new GesError("offline", { cause: lastError });
	}

	private async _parseJson<T>(response: Response): Promise<T> {
		try {
			return (await response.json()) as T;
		} catch (error) {
			throw new GesError("malformed", { cause: error });
		}
	}
}

export default EnterpriseService;
